/**
 * AuraHelpAssistant - Hidden help chatbot that appears when Aura logo is clicked
 * Provides guidance on using Axonion Aura Finance features
 */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import ConversationFeed from './ConversationFeed';
import { XMarkIcon, LifebuoyIcon } from '@heroicons/react/24/outline';

const PANEL_MOTION = {
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 6 },
  transition: { duration: 0.4, ease: 'easeOut' },
};

const AuraHelpAssistant = ({ isOpen, onClose, user }) => {
  const [cards, setCards] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && cards.length === 0) {
      // Add welcome card when opened
      const welcomeCard = {
        id: 'welcome',
        type: 'aura_reflection',
        title: 'Welcome to Aura Help',
        content: "Hi! I'm Aura, your guide to Axonion Aura Finance. I can help you:\n\n• Understand how to use different features\n• Navigate between sections (Cashflow, Vision, Learning, Game)\n• Get started with new features\n• Find the right tools for your financial goals\n\nWhat would you like to know?",
        timestamp: new Date().toISOString()
      };
      setCards([welcomeCard]);
    }
  }, [isOpen]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || loading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setLoading(true);

    try {
      // Use a simple guidance-focused endpoint or the unified chat
      const response = await axios.post('/api/aura-chat/chat', {
        message: userMessage,
        section: 'help',
        context: {
          help_mode: true,
          guidance_type: 'feature_guidance'
        }
      });

      if (response.data.success && response.data.cards) {
        setCards(prev => [...prev, ...response.data.cards]);
      } else {
        // Fallback response
        const fallbackCard = {
          id: `help_${Date.now()}`,
          type: 'aura_reflection',
          title: 'Help',
          content: "I'm here to help you navigate Axonion Aura Finance. Try asking about specific features like 'How do I use Cashflow?' or 'What is the Learning section?'",
          timestamp: new Date().toISOString()
        };
        setCards(prev => [...prev, fallbackCard]);
      }
    } catch (error) {
      console.error('Error sending help message:', error);
      const errorCard = {
        id: `error_${Date.now()}`,
        type: 'aura_reflection',
        title: 'Oops',
        content: "I'm having a bit of trouble right now. Try asking about features like Cashflow, Vision, Learning, or the Game!",
        timestamp: new Date().toISOString()
      };
      setCards(prev => [...prev, errorCard]);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          {...PANEL_MOTION}
          className="fixed bottom-4 right-4 w-96 max-w-[calc(100vw-2rem)] h-[600px] max-h-[calc(100vh-8rem)] surface-emphasis surface-dark z-50 flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 shrink-0 border-b border-hairline">
            <div className="flex items-center gap-3">
              <LifebuoyIcon className="w-5 h-5 text-secondary shrink-0" />
              <div>
                <h3 className="font-display text-h2 text-primary">Aura Help</h3>
                <p className="text-body text-secondary">Click the logo anytime for this help</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="icon-btn"
              aria-label="Close Aura Help"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Conversation Feed */}
          <div className="flex-1 overflow-y-auto p-4 [&_.aura-card]:light-surface [&_.aura-card]:bg-bone-raised [&_.aura-card]:border-hairline">
            <ConversationFeed
              cards={cards}
              user={user}
              section="help"
              loading={loading}
              onSaveNote={() => {}}
            />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-[color:var(--text-secondary)] border-opacity-30 shrink-0">
            <form onSubmit={handleSendMessage} className="flex gap-2">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder="Ask about features, navigation..."
                className="flex-1 px-3 py-2 rounded-md border border-[color:var(--text-secondary)] border-opacity-40 bg-pine/30 text-primary placeholder:text-secondary focus:outline-none focus:ring-2 focus:ring-[color:var(--text-primary)] text-body"
                disabled={loading}
              />
              <button
                type="submit"
                disabled={!inputMessage.trim() || loading}
                className="px-4 py-2 rounded-md bg-bone text-pine-deep font-medium text-body disabled:opacity-50 disabled:cursor-not-allowed transition-opacity duration-300"
              >
                Send
              </button>
            </form>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AuraHelpAssistant;
